import React from 'react';
import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import GroupIcon from '@mui/icons-material/Group';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { formatEventDateTime } from '../utils/eventTypes';
import PersonAvatar from '../shared/PersonAvatar';
import { UI_TIMINGS } from '../../../config/appConfig';

/**
 * SOIMatchEventListItem - Displays an SOI match event with reference image
 * @param {Object} event - Event data
 * @param {Function} onClick - Callback when item is clicked
 * @param {Function} onDelete - Callback when delete button is clicked
 * @param {boolean} disabled - Whether the item is disabled (e.g., during loading)
 * @param {boolean} showDelete - Whether to show the delete button
 */
function SOIMatchEventListItem({ event, onClick, onDelete, disabled = false, showDelete = true }) {
  const dateTimeStr = formatEventDateTime(event.createdAt);

  // Display "S.O.I <accuracy>%" instead of person ID
  const matchAccuracy = event.metadata?.matchAccuracy || 0;
  const accuracyPercent = Math.round(matchAccuracy * 100);
  const displayName = `S.O.I ${accuracyPercent}%`;

  // Backend provides full URLs - no conversion needed
  const personImage = event.metadata?.personImage;
  const matchSource = event.metadata?.matchSource;
  const capturedImageUrl = event.metadata?.capturedImageUrl;

  // Detect if this is a multi-face capture by counting person IDs in filename
  // Format: timestamp_personId1_personId2_personId3.jpg
  const isMultiFace = React.useMemo(() => {
    if (!capturedImageUrl) return false;
    const filename = capturedImageUrl.split('/').pop();
    const withoutExt = filename.replace(/\.(jpg|jpeg|png)$/i, '');
    const parts = withoutExt.split('_');
    return parts.length > 2;
  }, [capturedImageUrl]);

  const [swipeX, setSwipeX] = React.useState(0);
  const [isSwiping, setIsSwiping] = React.useState(false);
  const [isMouseDown, setIsMouseDown] = React.useState(false);
  const startX = React.useRef(0);
  const currentX = React.useRef(0);

  const handleDeleteClick = React.useCallback((e) => {
    e.stopPropagation();
    onDelete(event);
  }, [onDelete, event]);

  const handleClick = React.useCallback(() => {
    if (!isSwiping) {
      onClick(event);
    }
  }, [onClick, event, isSwiping]);

  const handleStart = (clientX) => {
    startX.current = clientX;
    currentX.current = clientX;
    setIsSwiping(false);
  };

  const handleMove = (clientX) => {
    currentX.current = clientX;
    const diff = currentX.current - startX.current;

    if (diff < 0) {
      setSwipeX(Math.max(diff, -100));
      if (Math.abs(diff) > 10) {
        setIsSwiping(true);
      }
    }
  };

  const handleEnd = () => {
    const diff = currentX.current - startX.current;

    if (diff < -60) {
      onDelete(event);
    } else {
      setSwipeX(0);
      setTimeout(() => setIsSwiping(false), UI_TIMINGS.SWIPE_ANIMATION_CLEANUP);
    }
    setIsMouseDown(false);
  };

  // Touch handlers
  const handleTouchStart = (e) => {
    handleStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    handleMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    handleEnd();
  };

  // Mouse handlers
  const handleMouseDown = (e) => {
    setIsMouseDown(true);
    handleStart(e.clientX);
  };

  const handleMouseMove = (e) => {
    if (isMouseDown) {
      handleMove(e.clientX);
    }
  };

  const handleMouseUp = () => {
    if (isMouseDown) {
      handleEnd();
    }
  };

  const handleMouseLeave = () => {
    if (isMouseDown) {
      handleEnd();
    }
  };

  // SOI-specific color scheme - red/error theme
  const isInAssignment = !!event.metadata?.assignmentEventId;
  const backgroundColor = isInAssignment
    ? '#f5f5f5'
    : '#ffebee'; // Light red for SOI matches
  const borderColor = isInAssignment
    ? '#ffcc80'
    : 'rgba(211, 47, 47, 0.5)'; // Red border for SOI matches

  return (
    <Box
      sx={{
        position: 'relative',
        mb: 1,
        overflow: 'hidden',
        borderRadius: 1
      }}
    >
      {/* SOI icon - top left corner */}
      <Tooltip title="Subject of Interest" arrow placement="right">
        <Box
          sx={{
            position: 'absolute',
            top: 4,
            left: 10,
            width: 24,
            height: 24,
            zIndex: 2,
            borderRadius: '4px',
            backgroundColor: 'error.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
          }}
        >
          <PersonSearchIcon sx={{ fontSize: 16, color: 'white' }} />
        </Box>
      </Tooltip>

      {/* Delete background - revealed during swipe */}
      <Box
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 100,
          backgroundColor: 'error.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          zIndex: 0
        }}
      >
        <DeleteIcon />
      </Box>

      {/* Main list item - slides left on swipe */}
      <ListItem
        disablePadding
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        sx={{
          position: 'relative',
          height: 80,
          backgroundColor: `${backgroundColor} !important`,
          borderLeft: `6px solid ${borderColor}`,
          borderRadius: 1,
          pl: 2,
          transform: `translateX(${swipeX}px)`,
          transition: isSwiping ? 'none' : 'transform 0.3s ease-out',
          cursor: isMouseDown ? 'grabbing' : 'grab',
          zIndex: 1,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: backgroundColor,
            zIndex: -1
          }
        }}
        secondaryAction={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Assignment indicator and match type indicators */}
            {(event.metadata?.assignmentEventId || matchSource === 'face_match') && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                {event.metadata?.assignmentEventId && (
                  <Tooltip title={event.metadata.assignmentEventId} arrow>
                    <AssignmentIcon sx={{ fontSize: 16, color: '#1976d2', opacity: 0.7 }} />
                  </Tooltip>
                )}
                {matchSource === 'face_match' && (
                  <AutoAwesomeIcon
                    sx={{
                      color: 'error.main',
                      fontSize: 16
                    }}
                  />
                )}
                {matchSource === 'face_match' && isMultiFace && (
                  <GroupIcon
                    sx={{
                      color: 'error.main',
                      fontSize: 16
                    }}
                  />
                )}
              </Box>
            )}
            {false && showDelete && (
              <IconButton
                edge="end"
                aria-label="delete"
                size="small"
                onClick={handleDeleteClick}
                disabled={disabled}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        }
      >
        <ListItemButton
          onClick={handleClick}
          disabled={disabled}
          disableRipple={isSwiping}
          sx={{
            height: '100%',
            backgroundColor: 'inherit',
            '&:hover': {
              backgroundColor: 'inherit'
            },
            '& .MuiTouchRipple-root': {
              display: 'none'
            }
          }}
        >
          <Box
            sx={{
              border: '3px solid',
              borderColor: 'error.main',
              borderRadius: '50%',
              padding: '2px',
              display: 'inline-flex',
              flexShrink: 0,
              mr: 2,
              '& .MuiAvatar-root': { mr: 0 }
            }}
          >
            <PersonAvatar imageUrl={personImage} name={displayName} size={64} />
          </Box>
          <ListItemText
            primary={displayName}
            secondary={dateTimeStr}
          />
        </ListItemButton>
      </ListItem>
    </Box>
  );
}

export default React.memo(SOIMatchEventListItem);
